import {
  defineHostPageFunnelEvent,
  defineHostPageUIElementEvent,
  HOST_PAGE_EVENT_FLUSH_MODES,
} from './host-page-event.instrumentation'
import {
  HOST_PAGE_EVENT_NAMES,
  HOST_PAGE_MPARTICLE_EVENT_TYPES,
  HostPageEventData,
} from './host-page-event.types'

export interface AbandonCartActionEventData extends HostPageEventData {
  text: string
  tracking_id: string
}

export interface HostedDonationGridViewedEventData extends HostPageEventData {
  donation_amount_default: number
  donation_amount_options: string
  donation_currency_default: string
  donation_currency_options: string
  donation_frequency_default: string
  donation_frequency_options: string
  donation_amount_options_set_id?: string
}

export interface StudioGridFrequencyToggleEventData extends HostPageEventData {
  from_frequency?: string
  to_frequency: string
}

export interface StudioGridAmountSelectionEventData extends HostPageEventData {
  donation_amount?: number
  value: string
}

const DONATION_AMOUNT_TRACKING_ID = 'donation amount'

export interface StudioGridCurrencySelectionEventData extends HostPageEventData {
  from_currency?: string
  to_currency: string
}

const DONATION_CURRENCY_TRACKING_ID = 'donation currency'

const DONATE_CTA_TRACKING_ID = 'donation grid cta'

export interface DonateCTAClickedEventData extends HostPageEventData {
  donation_amount: number
  donation_currency: string
  donation_frequency: string
  link_url: string
  text: string
}

export interface StudioGridInteractionEventData extends HostPageEventData {
  tracking_id: string
}

export const donateCTAClickedEvent = defineHostPageUIElementEvent<DonateCTAClickedEventData>({
  buildMParticleEventInfo: ({ data }) => ({
    donation_amount: data.donation_amount,
    donation_currency: data.donation_currency,
    donation_frequency: data.donation_frequency,
    link_url: data.link_url,
    outbound: false,
    text: data.text,
    tracking_id: DONATE_CTA_TRACKING_ID,
  }),
  flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
  hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_PRIMARY_CTA_CLICK,
  mparticleEventName: 'Donate CTA Clicked',
  mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
  source: 'inline-donation-grid',
})

export const abandonCartActionEvent = defineHostPageUIElementEvent<AbandonCartActionEventData>({
  buildMParticleEventInfo: ({ data }) => ({
    placement: 'hosted page',
    text: data.text,
    tracking_id: data.tracking_id,
  }),
  flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
  hostPageEventName: HOST_PAGE_EVENT_NAMES.ABANDON_CART_NUDGE,
  mparticleEventName: 'Button Clicked',
  mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
  source: 'abandon-cart-nudge',
})

const makeStudioGridInteractionEvent = (mparticleEventName: 'Button Clicked' | 'Value Selected') =>
  defineHostPageUIElementEvent<StudioGridInteractionEventData>({
    buildMParticleEventInfo: ({ data }) => ({
      tracking_id: data.tracking_id,
    }),
    flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
    hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_INTERACTION,
    mparticleEventName,
    mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
    source: 'inline-donation-grid',
  })

export const studioGridButtonClickedEvent = makeStudioGridInteractionEvent('Button Clicked')
export const studioGridValueSelectedEvent = makeStudioGridInteractionEvent('Value Selected')

export const hostedDonationGridViewedEvent =
  defineHostPageFunnelEvent<HostedDonationGridViewedEventData>({
    buildMParticleEventInfo: ({ data }) => ({
      donation_amount_default: data.donation_amount_default,
      donation_amount_options: data.donation_amount_options,
      donation_currency_default: data.donation_currency_default,
      donation_currency_options: data.donation_currency_options,
      donation_frequency_default: data.donation_frequency_default,
      donation_frequency_options: data.donation_frequency_options,
      ...(data.donation_amount_options_set_id
        ? { donation_amount_options_set_id: data.donation_amount_options_set_id }
        : {}),
    }),
    flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
    hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_VIEWED,
    mparticleEventName: 'Hosted Donation Grid Viewed',
    mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
    source: 'inline-donation-grid',
  })

export const eg3SDKLoadEvent = defineHostPageFunnelEvent({
  flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
  hostPageEventName: HOST_PAGE_EVENT_NAMES.EG3_SDK_LOAD,
  mparticleEventName: 'EG3 SDK Loaded',
  mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
  source: 'inline-donation-grid',
  buildMParticleEventInfo: () => ({
    entry_source: 'hosted_checkout',
  }),
})

const toFrequencyToggleValue = (frequency: string): 'Monthly' | 'One-time' => {
  return frequency.toLowerCase() === 'monthly' ? 'Monthly' : 'One-time'
}

export const studioGridFrequencyToggleEvent =
  defineHostPageUIElementEvent<StudioGridFrequencyToggleEventData>({
    buildMParticleEventInfo: ({ data }) => ({
      value: toFrequencyToggleValue(data.to_frequency),
    }),
    flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
    hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_FREQUENCY_CHANGE,
    mparticleEventName: 'Frequency Toggle',
    mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
    source: 'inline-donation-grid',
  })

export const studioGridAmountSelectionEvent =
  defineHostPageUIElementEvent<StudioGridAmountSelectionEventData>({
    buildMParticleEventInfo: ({ data }) => ({
      ...(typeof data.donation_amount === 'number'
        ? { donation_amount: data.donation_amount }
        : {}),
      tracking_id: DONATION_AMOUNT_TRACKING_ID,
      value: data.value,
    }),
    flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
    hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_AMOUNT_CHANGE,
    mparticleEventName: 'Value Selected',
    mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
    source: 'inline-donation-grid',
  })

export const studioGridCurrencySelectionEvent =
  defineHostPageUIElementEvent<StudioGridCurrencySelectionEventData>({
    buildMParticleEventInfo: ({ data }) => ({
      tracking_id: DONATION_CURRENCY_TRACKING_ID,
      value: data.to_currency,
    }),
    flushMode: HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE,
    hostPageEventName: HOST_PAGE_EVENT_NAMES.STUDIO_GRID_CURRENCY_CHANGE,
    mparticleEventName: 'Value Selected',
    mparticleEventType: HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
    source: 'inline-donation-grid',
  })
